import { blobToHex, divideArrayIntoChunks } from '@pkg/basic'
import type { InternalTransaction, TransactionIndex } from '@pkg/evm'
import { and, eq, gte, inArray } from 'drizzle-orm'
import { sqliteMaxHostParameterCount } from '../constants'
import {
  accountEvmTxIndexRelationTable,
  accountTable,
  evmInternalTransactionTable,
  evmTxIndexTable,
} from '../schema'
import type { DatabaseWithTransaction } from '../types'

type GetEvmTxIndexesParameters = {
  readonly database: DatabaseWithTransaction
  readonly chainId: number
  readonly analyzed: boolean
  readonly startTime?: number
}

type GetEvmTxIndexesReturnTypes = {
  readonly indexes: readonly TransactionIndex[]
  readonly internalTransactions: readonly InternalTransaction[]
}

/**
 * 指定チェーンの解析状態と開始日時に合う取引索引・内部取引を取得する。
 * @param params DB、チェーン、解析状態、任意の開始日時（ミリ秒）。
 * @returns 開始日時以降の索引と、その索引に対応する内部取引。
 */
export const getEvmTxIndexes = async ({
  database: { database },
  chainId,
  analyzed,
  startTime,
}: GetEvmTxIndexesParameters): Promise<GetEvmTxIndexesReturnTypes> => {
  const hashRows = await database
    .selectDistinct({ hash: accountEvmTxIndexRelationTable.hash })
    .from(accountEvmTxIndexRelationTable)
    .leftJoin(
      accountTable,
      eq(accountEvmTxIndexRelationTable.accountId, accountTable.id),
    )
    .where(
      and(
        eq(accountEvmTxIndexRelationTable.analyzed, analyzed),
        eq(accountTable.evmChainId, chainId),
      ),
    )
  if (hashRows.length === 0) {
    return {
      indexes: [],
      internalTransactions: [],
    }
  }

  const hashes = hashRows.map(({ hash }) => hash)

  const hashChunks = divideArrayIntoChunks(
    hashes,
    sqliteMaxHostParameterCount - 2,
  )
  const indexRows: (typeof evmTxIndexTable.$inferSelect)[] = []
  const internalTxRows: (typeof evmInternalTransactionTable.$inferSelect)[] = []
  for (const chunk of hashChunks) {
    const indexChunkRows = await database
      .select()
      .from(evmTxIndexTable)
      .where(
        and(
          eq(evmTxIndexTable.chainId, chainId),
          inArray(evmTxIndexTable.hash, chunk),
          startTime === undefined
            ? undefined
            : gte(evmTxIndexTable.timestamp, new Date(startTime)),
        ),
      )
    indexRows.push(...indexChunkRows)
    if (indexChunkRows.length === 0) {
      continue
    }
    const internalTxChunkRows = await database
      .select()
      .from(evmInternalTransactionTable)
      .where(
        and(
          eq(evmInternalTransactionTable.chainId, chainId),
          inArray(
            evmInternalTransactionTable.hash,
            indexChunkRows.map(({ hash }) => hash),
          ),
        ),
      )
    internalTxRows.push(...internalTxChunkRows)
  }

  return {
    indexes: indexRows
      .map(
        (row): TransactionIndex => ({
          hash: blobToHex(row.hash),
          blockNumber: row.blockNumber,
          timestamp: row.timestamp.getTime(),
          input: row.input === null ? undefined : blobToHex(row.input),
          value: row.value === null ? undefined : BigInt(blobToHex(row.value)),
        }),
      )
      .sort((a, b) => b.timestamp - a.timestamp),
    internalTransactions: internalTxRows.map(
      (row): InternalTransaction => ({
        contractAddress:
          row.contractAddress.length === 0
            ? undefined
            : blobToHex(row.contractAddress),
        from: blobToHex(row.from),
        value: BigInt(blobToHex(row.value)),
        gas: BigInt(blobToHex(row.gas)),
        isError: row.isError,
        to: row.to.length === 0 ? undefined : blobToHex(row.to),
        txHash: blobToHex(row.hash),
      }),
    ),
  }
}
